import { type Faro, type TransportItem, TransportItemType } from "@grafana/faro-web-sdk";
import { match } from "ts-pattern";

// Good-pageload thresholds for the SLO. The values mirror the SLO definition
// (LCP ≤ 2.5s / INP ≤ 200ms) and happen to match the web-vitals "good" rating,
// but we judge raw values against our own thresholds instead of relying on the
// rating string, so the SLO definition stays the single source of truth.
const LCP_GOOD_THRESHOLD_MS = 2500;
const INP_GOOD_THRESHOLD_MS = 200;

// Identifier of the measurement we send. The collector (Alloy) maps this type
// and the value keys below to cumulative counters (frontend_pageload_total /
// frontend_pageload_good_total); changing any of them breaks that mapping, so
// coordinate changes with the collector config.
export const PAGELOAD_SLO_MEASUREMENT_TYPE = "faro_pageload_slo";

// OTel span status: UNSET=0 / OK=1 / ERROR=2. The fetch/XHR instrumentation
// marks both network failures and 4xx/5xx as ERROR, so the presence of an
// ERROR span serves as a proxy for request failure.
const SPAN_STATUS_ERROR = 2;

// SLO evidence observed during a single page load. The browser only knows
// about its own load (aggregation is the receiver's job), so all we track here
// is whether this load showed any bad signal.
export interface PageloadSloState {
  // null = not measured; boolean = good/bad. LCP is almost always measured.
  lcpGood: boolean | null;
  // INP is never measured without user interaction; treat that as
  // "no slow interaction = good".
  inpGood: boolean | null;
  hadError: boolean;
  hadRequestFailure: boolean;
}

export function createPageloadSloState(): PageloadSloState {
  return {
    lcpGood: null,
    inpGood: null,
    hadError: false,
    hadRequestFailure: false,
  };
}

function observeWebVitals(state: PageloadSloState, payload: unknown): PageloadSloState {
  const measurement = payload as {
    type?: string;
    values?: Record<string, number>;
  };
  if (measurement.type !== "web-vitals" || !measurement.values) {
    return state;
  }
  const next = { ...state };
  if ("lcp" in measurement.values) {
    next.lcpGood = measurement.values.lcp <= LCP_GOOD_THRESHOLD_MS;
  }
  if ("inp" in measurement.values) {
    next.inpGood = measurement.values.inp <= INP_GOOD_THRESHOLD_MS;
  }
  return next;
}

function observeTraceForRequestFailure(state: PageloadSloState, payload: unknown): PageloadSloState {
  const trace = payload as {
    resourceSpans?: Array<{
      scopeSpans?: Array<{
        spans?: Array<{ status?: { code?: number } }>;
      }>;
    }>;
  };
  for (const resourceSpan of trace.resourceSpans ?? []) {
    for (const scopeSpan of resourceSpan.scopeSpans ?? []) {
      for (const span of scopeSpan.spans ?? []) {
        if (span.status?.code === SPAN_STATUS_ERROR) {
          return { ...state, hadRequestFailure: true };
        }
      }
    }
  }
  return state;
}

// Pure function: inspects one item Faro is about to send and returns the
// updated SLO evidence. Mutates neither the state nor the item (observe only).
export function observeTransportItem(state: PageloadSloState, item: TransportItem): PageloadSloState {
  return match(item.type)
    .with(TransportItemType.MEASUREMENT, () => observeWebVitals(state, item.payload))
    .with(TransportItemType.EXCEPTION, () => ({ ...state, hadError: true }))
    .with(TransportItemType.TRACE, () => observeTraceForRequestFailure(state, item.payload))
    .otherwise(() => state);
}

// SLO definition of good: LCP is good, INP is not bad (unmeasured is
// acceptable), no exceptions, and no request failures. An unmeasured LCP is
// not good: LCP is normally always measured, so its absence is itself an
// anomaly and the SLO must not miss bad signals.
export function isPageloadGood(state: PageloadSloState): boolean {
  return state.lcpGood === true && state.inpGood !== false && !state.hadError && !state.hadRequestFailure;
}

// Wires SLO tracking into Faro: observes evidence via beforeSend and sends
// "total 1 / good 0-or-1" once when the page becomes hidden. Building the
// increments into cumulative counters is the collector's job; the browser only
// sends the increment for its own load. Returns a function that unwires
// everything (cleanup for tests / rewiring).
export function trackPageloadSlo(faro: Faro, labels: { service: string; env: string }): () => void {
  let state = createPageloadSloState();

  const beforeSend = (item: TransportItem) => {
    try {
      state = observeTransportItem(state, item);
    } catch {
      // Swallow observation failures so they never break the actual send.
      // SLO is best-effort.
    }
    return item;
  };
  faro.transports.addBeforeSendHooks(beforeSend);

  // Hidden can fire multiple times per load (tab switches), but one load = one
  // increment, so send only once.
  let reported = false;
  const report = () => {
    if (reported) {
      return;
    }
    reported = true;
    const good = isPageloadGood(state);
    try {
      faro.api.pushMeasurement(
        {
          type: PAGELOAD_SLO_MEASUREMENT_TYPE,
          // Metric value keys follow the Prometheus/Mimir snake_case convention.
          /* eslint-disable camelcase */
          values: {
            pageload_total: 1,
            pageload_good: good ? 1 : 0,
          },
          /* eslint-enable camelcase */
        },
        { context: labels },
      );
    } catch {
      // Never leak send exceptions out of the event handler. SLO is
      // best-effort, and throwing here would surface as an uncaught error in
      // visibilitychange / pagehide.
    }
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      report();
    }
  };
  document.addEventListener("visibilitychange", onVisibilityChange);
  // Fallback for bfcache eviction and browsers that skip visibilitychange on
  // some navigations.
  window.addEventListener("pagehide", report);

  return () => {
    faro.transports.removeBeforeSendHooks(beforeSend);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", report);
  };
}
